import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, notification, Form, Space ,Tooltip, Modal,Input} from "antd";
import GenericTable from "../components/GenericTable";
import GenericDrawer from "../components/GenericModal";
import GenericViewModal from "../components/GenericViewModal";
import {
  fetchServicesRequest,
  createServiceRequest,
  updateServiceRequest,
  deleteServiceRequest,
} from "../redux-saga/actions/serviceActions";
import { useParams } from "react-router-dom";
import ProjectNavbar from "../components/ProjectNavbar";
import { updateCurrentProjectId } from "../redux-saga/actions/customerActions";
import {fetchPhysiciansRequest}  from "../redux-saga/actions/physicianActions";
import {fetchDiagnosesRequest} from "../redux-saga/actions/diagnosisActions";
import moment from "moment/moment";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {IconReportMedical} from '@tabler/icons-react';

const ServicePage = () => {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const services = useSelector((state) => state.service.services?.data);
  const loading = useSelector((state) => state.service.loading);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [form] = Form.useForm();
  const [viewingService, setViewingService] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const physicians = useSelector((state) => state.physician.physicians?.data);
  const diagnoses = useSelector((state) => state.diagnosis.diagnoses?.data);

  const metaData = useSelector((state) => state.service.services?.meta || {});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchServicesRequest(projectId,{ page: currentPage, limit: 2, search: searchTerm }));
    dispatch(fetchPhysiciansRequest(projectId));
    dispatch(fetchDiagnosesRequest(projectId));
    dispatch(updateCurrentProjectId(projectId));
  }, [dispatch, projectId,currentPage, searchTerm]);

  const serviceModalInputs = [
    { label: "Sequence", name: "sequence", type: "number" },
    { label: "CPT Code ID", name: "cptCodeId", type: "text" },
    { label: "Description", name: "description", type: "text" },
    { label: "Modifier", name: "modifier", type: "text" },
    { label: "Units", name: "units", type: "number" },
    { label: "Type", name: "type", type: "text" },
    { label: "Date of Service (DOS)", name: "DOS", type: "date" },
    { label: "Place of Service (POS)", name: "POS", type: "date" },
    { label: "Charges", name: "charges", type: "number" },
    { label: "Physician ID", name: "physicianId", type: "select",options: physicians?.map((physician) => ({
      label: physician.name,
      value: physician.id,
    })), },
    { label: "Diagnosis ID", name: "diagnosisId", type: "select",options: diagnoses?.map((diagnose) => ({
      label: diagnose.name,
      value: diagnose.id,
    })), },
  ];

  const handleCreateOrEdit = async (values) => {
    const payload = {
      sequence: values.sequence !== undefined ? Number(values.sequence) : 0,
      cptCodeId: values.cptCodeId || "",
      description: values.description || "",
      modifier: values.modifier || "",
      units: values.units !== undefined ? Number(values.units) : 0,
      type: values.type || "",
      DOS: values.DOS ? values.DOS.toISOString() : null,   // Ensure date is an ISO string
      POS: values.POS ? values.POS.toISOString() : null,
      charges: values.charges !== undefined ? Number(values.charges) : 0,
      physicianId: values.physicianId || "",
      diagnosisId: values.diagnosisId || ""
    };
  
    try {
      if (editingService) {
         dispatch(updateServiceRequest(editingService.id, payload, projectId));
      } else {
         dispatch(createServiceRequest(payload, projectId));
      }
      notification.success({ message: "Success", description: "Service saved successfully." });
      setIsModalVisible(false);
      setEditingService(null);
      form.resetFields();
    } catch (error) {
      console.error("Error saving service:", error);
      notification.error({
        message: "Error",
        description: error.response?.data?.message || "An unexpected error occurred. Please try again.",
      });
    }
  };
  
  

  const handleView = (record) => {
    const formattedRecord = {
      ...record,
      DOS: record.DOS ? moment(record.DOS).format("YYYY-MM-DD HH:mm:ss") : null,
      POS: record.POS ? moment(record.POS).format("YYYY-MM-DD HH:mm:ss") : null,
      createdAt: record.createdAt ? moment(record.createdAt).format("YYYY-MM-DD HH:mm:ss") : null,
      updatedAt: record.updatedAt ? moment(record.updatedAt).format("YYYY-MM-DD HH:mm:ss") : null,
    };
    setViewingService(formattedRecord);
    setIsViewModalVisible(true);
  };

  const handleEdit = (record) => {
    const formattedRecord = {
      ...record,
      DOS: record.DOS ? moment(record.DOS) : null,
      POS: record.POS ? moment(record.POS) : null,
    };
    setEditingService(formattedRecord);
    setIsModalVisible(true);
    form.setFieldsValue(record);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Confirm",
      content: "Are you sure you want to delete this service?",
      onOk: () => {
        dispatch(deleteServiceRequest(record.id, projectId));
      },
    });
  };

  const columnsConfig = [
    { title: "Sequence", dataIndex: "sequence" },
    { title: "CPT Code ID", dataIndex: "cptCodeId" },
    { title: "Description", dataIndex: "description" },
    // { title: "Modifier", dataIndex: "modifier" },
    { title: "Units", dataIndex: "units" },
    // { title: "Type", dataIndex: "type" },
    { title: "DOS", dataIndex: "DOS" },
    { title: "POS", dataIndex: "POS" },
    { title: "Charges", dataIndex: "charges" },
    // { title: "Physician ID", dataIndex: "physicianId" },
    // { title: "Diagnosis ID", dataIndex: "diagnosisId" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <EyeOutlined style={{ fontSize: "20px", color: "#1890ff" }} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <EditOutlined style={{ fontSize: "20px", color: "#faad14" }} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined style={{ fontSize: "20px", color: "#f5222d" }} onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];


  return (
    <div style={{display:"flex",gap:"10px"}}>
       <ProjectNavbar />
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <IconReportMedical style={{ fontSize: "24px", color: "black" }} />
                <h2 style={{ margin: 0 }}>Service</h2>
            </div>

            <div style={{ display: "flex", justifyContent: "end", marginBottom: 11 }}>
              <Button type="primary" onClick={() => setIsModalVisible(true)}>
                Create Service
              </Button>
            </div>
        </div>
        <Input
          placeholder="Search Batch"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: "16px" }}
        />
      <GenericTable data={services} columnsConfig={columnsConfig} loading={loading}
      pagination={{
        current: metaData.page || 1,
        pageSize: metaData.limit || 2,
        total: metaData.total || 0,
         onChange: (page, pageSize) => {
          setCurrentPage(page);
          dispatch(fetchServicesRequest({ page, limit: pageSize, search: searchTerm }));
        },
      }} />
      {isModalVisible && (
          <GenericDrawer
            isVisible={isModalVisible}
            handleCancel={() => {
              setIsModalVisible(false);
              setEditingService(null);
            }}
            title={
              <span style={{display : "flex",alignItems:"center"}}>
                <IconReportMedical style={{ color: "white", marginRight: "8px" }} />
                {editingService ? "Edit Service" : "Create Service"}
              </span>
            }
            inputs={serviceModalInputs}
            onSubmit={handleCreateOrEdit}
            predefinedData={editingService || {}}
          />
        )}
      {isViewModalVisible && (
        <GenericViewModal
          isVisible={isViewModalVisible}
          handleCancel={() => setIsViewModalVisible(false)}
          title="View Service"
          data={viewingService}
          footerButtons={["Close"]}
          onButtonClick={() => setIsViewModalVisible(false)}
        />
      )}
      </div>
    </div>
  );
};

export default ServicePage;
